import React, { useMemo } from "react";
import { Field, Formik, Form } from "formik";
import mapValues from "lodash/mapValues";
import { validateExternalIdRequiresArn } from "integrations/aws/utils/awsValidation";
import { DATA_LAKE_CONFIG_ACTION } from "data-lake/Types";
import { Modal, Input, HelpBlock, Alert } from "components/bootstrap";
import { FormikFormGroup, ModalSubmit, Select } from "components/common";
import { FILESYSTEM_TYPE, S3_TYPE, GCS_TYPE, ABS_TYPE } from "archive/ArchiveStore";
import {
  S3_COMPATIBLE_OPTIONS,
  getS3Variant,
  isS3CompatibleType,
  resolveS3CompatibleType
} from "archive/logic/s3CompatibleProviders";
import {
  useCreateDataLakeBackend,
  useUpdateDataLakeBackend
} from "data-lake/data-lake-backend-config/hooks/useDataLakeBackendMutation";
import useDataLakeBackend from "data-lake/data-lake-backend-config/hooks/useDataLakeBackend";
import ArchiveBackendNameDescriptionFormGroup from "archive/components/ArchiveBackendNameDescriptionFormGroup";
import ArchiveBackendS3FieldGroup from "archive/components/ArchiveBackendS3FieldGroup";
import ArchiveBackendGCSFieldGroup from "archive/components/ArchiveBackendGCSFieldGroup";
import ArchiveBackendOutputPathFieldGroup from "archive/components/ArchiveBackendOutputPathFieldGroup";
import ArchiveBackendABSFieldGroup from "archive/components/ArchiveBackendABSFieldGroup";
import AWSRegionsFormGroup from "archive/components/aws/AWSRegionsFormGroup";
import ArchiveActions from "archive/ArchiveActions";
import DataLakeBackendWarning from "data-lake/data-lake-backend-config/DataLakeBackendWarning";
import GCSSetupInfo from "components/gcs/GCSSetupInfo";
const formatBackendValidationErrors = (backendErrors) => {
  const backendErrorStrings = mapValues(backendErrors, (errorArray) => `${errorArray.join(" ")}`);
  const { title, description, ...rest } = backendErrorStrings;
  return {
    title: title || void 0,
    description: description || void 0,
    settings: {
      ...rest
    }
  };
};
const validateBackendForm = (values) => {
  var _a;
  const errors = {};
  if (((_a = values.settings) == null ? void 0 : _a.type) === S3_TYPE) {
    const s3Settings = values.settings;
    const externalIdError = validateExternalIdRequiresArn(s3Settings == null ? void 0 : s3Settings.aws_assume_role_arn, s3Settings == null ? void 0 : s3Settings.aws_external_id);
    if (externalIdError) {
      errors.settings = {
        ...errors.settings,
        aws_external_id: externalIdError
      };
    }
  }
  return errors;
};
const DataLakeBackendFormModal = ({ backendId = void 0, onClose }) => {
  const action = backendId ? DATA_LAKE_CONFIG_ACTION.Update : DATA_LAKE_CONFIG_ACTION.Create;
  const modalTitle = action === DATA_LAKE_CONFIG_ACTION.Update ? "Edit Data Lake Backend" : "Create Data Lake Backend";
  const submitButtonText = action === DATA_LAKE_CONFIG_ACTION.Update ? "Update" : "Create";
  const submitLoadingText = action === DATA_LAKE_CONFIG_ACTION.Update ? "Updating..." : "Creating...";
  const BACKEND_TYPE_OPTIONS = [
    { value: FILESYSTEM_TYPE, label: "File system" },
    { value: S3_TYPE, label: "S3" },
    ...S3_COMPATIBLE_OPTIONS,
    { value: GCS_TYPE, label: "GCS" },
    { value: ABS_TYPE, label: "Azure Blob" }
  ];
  const { data: backend, isSuccess: isBackendLoaded } = useDataLakeBackend(backendId, !!backendId);
  const { mutateAsync: createBackend, isLoading: isCreatingBackend } = useCreateDataLakeBackend(onClose);
  const { mutateAsync: updateBackend, isLoading: isUpdatingBackend } = useUpdateDataLakeBackend(onClose);
  const prepareInitialValues = (existingBackend) => {
    var _a, _b, _c;
    return {
      ...(existingBackend == null ? void 0 : existingBackend.id) && { id: existingBackend.id },
      title: (_a = existingBackend == null ? void 0 : existingBackend.title) != null ? _a : "",
      description: (_b = existingBackend == null ? void 0 : existingBackend.description) != null ? _b : "",
      settings: (_c = existingBackend == null ? void 0 : existingBackend.settings) != null ? _c : { type: void 0 }
    };
  };
  const initialValues = useMemo(() => prepareInitialValues(backend), [backend]);
  if (backendId && !isBackendLoaded) {
    return null;
  }
  const setFormErrors = (error, setErrors) => {
    var _a, _b;
    if (typeof ((_b = (_a = error == null ? void 0 : error.additional) == null ? void 0 : _a.body) == null ? void 0 : _b.errors) === "object") {
      setErrors(formatBackendValidationErrors(error.additional.body.errors));
    }
  };
  const handleSubmit = (values, { setErrors }) => {
    const resolvedValues = {
      ...values,
      settings: { ...values.settings, type: resolveS3CompatibleType(values.settings.type) }
    };
    if (action === DATA_LAKE_CONFIG_ACTION.Update) {
      updateBackend({ id: backendId, ...resolvedValues }).catch((error) => {
        setFormErrors(error, setErrors);
      });
    } else {
      createBackend(resolvedValues).catch((error) => {
        setFormErrors(error, setErrors);
      });
    }
  };
  const checkOutputPath = (path) => ArchiveActions.checkOutputTemplate(path);
  return /* @__PURE__ */ React.createElement(Modal, { onHide: onClose, bsSize: "large", show: true }, /* @__PURE__ */ React.createElement(Formik, { initialValues, onSubmit: handleSubmit, validate: validateBackendForm }, ({ values, setFieldValue, isSubmitting }) => /* @__PURE__ */ React.createElement(Form, null, /* @__PURE__ */ React.createElement(Modal.Header, null, /* @__PURE__ */ React.createElement(Modal.Title, null, modalTitle)), /* @__PURE__ */ React.createElement(Modal.Body, null, /* @__PURE__ */ React.createElement(DataLakeBackendWarning, null), values.settings.type === S3_TYPE && /* @__PURE__ */ React.createElement(Alert, { bsStyle: "warning" }, "Note that only ", /* @__PURE__ */ React.createElement("b", null, "Amazon S3 Buckets"), " are currently supported."), values.settings.type === GCS_TYPE && /* @__PURE__ */ React.createElement(GCSSetupInfo, null), /* @__PURE__ */ React.createElement(Field, { name: "settings.type" }, () => /* @__PURE__ */ React.createElement(
    Input,
    {
      help: "Select the Backend type you want to configure.",
      id: "default-backend-type-select",
      label: "Backend Type"
    },
    /* @__PURE__ */ React.createElement(
      Select,
      {
        inputId: "default-backend-type-select",
        name: "BackendType",
        placeholder: "Select Backend Type",
        options: BACKEND_TYPE_OPTIONS,
        disabled: !!backendId,
        onChange: (option) => {
          setFieldValue("settings", { type: option, output_path: values.settings.output_path });
        },
        value: values.settings.type
      }
    ),
    /* @__PURE__ */ React.createElement(HelpBlock, null)
  )), values.settings.type && /* @__PURE__ */ React.createElement(ArchiveBackendNameDescriptionFormGroup, null), values.settings.type === FILESYSTEM_TYPE && /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      type: "number",
      name: "settings.usage_threshold",
      label: "Disk usage threshold (%)",
      min: 0,
      max: 100,
      help: "The percentage of disk utilization that should trigger a notification.",
      labelClassName: "",
      wrapperClassName: ""
    }
  ), isS3CompatibleType(values.settings.type) && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(ArchiveBackendS3FieldGroup, { enableSpool: false, variant: getS3Variant(values.settings.type) }), /* @__PURE__ */ React.createElement(AWSRegionsFormGroup, { variant: getS3Variant(values.settings.type) })), values.settings.type === GCS_TYPE && /* @__PURE__ */ React.createElement(ArchiveBackendGCSFieldGroup, { enableSpool: false }), values.settings.type === ABS_TYPE && /* @__PURE__ */ React.createElement(ArchiveBackendABSFieldGroup, { enableSpool: false }), values.settings.type && /* @__PURE__ */ React.createElement(ArchiveBackendOutputPathFieldGroup, { checkOutputPath, noPreview: true })), /* @__PURE__ */ React.createElement(Modal.Footer, null, values.settings.type && /* @__PURE__ */ React.createElement(
    ModalSubmit,
    {
      submitButtonText,
      submitLoadingText,
      onCancel: onClose,
      disabledSubmit: isCreatingBackend || isUpdatingBackend,
      isSubmitting
    }
  )))));
};
export default DataLakeBackendFormModal;
